import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AxiosError } from 'axios'
import luminaireAPI from '../service/luminaireAPI'
import { Luminaire, LuminaireMetric, LuminaireMetricFilter } from '../types/luminaire.types'

export type CallbackFunction = (result: { result?: any; error?: any }) => void

export interface UseLuminairesParams {
  page?: number
  pageSize?: number
  search?: string
  tags?: string[]
}

export const useLuminaireByDevEui = (devEui?: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ['luminaires-fleet', 500, ''],
    queryFn: () => luminaireAPI.getAllLuminaires(0, 500),
    staleTime: 60_000,
    enabled: Boolean(devEui),
  })
  const luminaire = (data?.data ?? []).find((l) => l.lum_dev_eui === devEui)
  return { luminaire, isLoading }
}

export const useKnownLuminaireTags = () => {
  const { data } = useQuery({
    queryKey: ['luminaires-fleet', 500, ''],
    queryFn: () => luminaireAPI.getAllLuminaires(0, 500),
    staleTime: 60_000,
  })
  const tags = useMemo(() => {
    const set = new Set<string>()
    for (const l of data?.data ?? []) {
      for (const t of l.lum_tags ?? []) set.add(t)
    }
    return Array.from(set).sort()
  }, [data])
  return tags
}

export const useLuminaires = ({
  page = 0,
  pageSize = 50,
  search = '',
  tags = [],
}: UseLuminairesParams = {}) => {
  const tagsKey = tags.join(',')
  const luminairesQuery = useQuery({
    queryKey: ['luminaires', page, pageSize, search, tagsKey],
    queryFn: () =>
      luminaireAPI.getAllLuminaires(
        page,
        pageSize,
        search || undefined,
        tags.length > 0 ? tags : undefined,
      ),
  })

  return {
    luminairesQuery,
  }
}

export const useLuminaireMetric = (
  metric: LuminaireMetric,
  filter: LuminaireMetricFilter
) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['luminaire-metric', metric, filter],
    queryFn: () => luminaireAPI.getLuminaireMetric(metric, filter),
    enabled: !!filter.devEui,
  })

  return { data, isLoading, isError }
}

export const useCreateLuminaire = () => {
  const queryClient = useQueryClient()

  const createLuminaire = useMutation({
    mutationFn: ({
      luminaire,
      callbackFunc,
    }: {
      luminaire: Omit<Luminaire, 'lum_id'>
      callbackFunc?: CallbackFunction
    }) =>
      luminaireAPI.createLuminaire(luminaire).then((result) => {
        queryClient.invalidateQueries('luminaires')
        if (callbackFunc) {
          callbackFunc({ result, error: undefined })
        }
      }).catch((error) => {
        if (callbackFunc) {
          callbackFunc({ result: undefined, error })
        }
        return Promise.reject(error)
      }),
    onSettled: async () => {
      queryClient.invalidateQueries('luminaires')
    },
  })

  return {
    mutate: createLuminaire.mutate,
    isLoading: createLuminaire.isLoading,
    isSuccess: createLuminaire.isSuccess,
    isError: createLuminaire.isError,
    error: createLuminaire.error as AxiosError,
  }
}

export const useUpdateLuminaire = () => {
  const queryClient = useQueryClient()

  const updateLuminaire = useMutation({
    mutationFn: ({
      lumId,
      luminaire,
      callbackFunc,
    }: {
      lumId: string
      luminaire: Luminaire
      callbackFunc?: CallbackFunction
    }) =>
      luminaireAPI.updateLuminaire(lumId, luminaire).then((result) => {
        queryClient.invalidateQueries('luminaires')
        if (callbackFunc) {
          callbackFunc({ result, error: undefined })
        }
      }).catch((error) => {
        if (callbackFunc) {
          callbackFunc({ result: undefined, error })
        }
        return Promise.reject(error)
      }),
    onSettled: async () => {
      queryClient.invalidateQueries('luminaires')
    },
  })

  return {
    mutate: updateLuminaire.mutate,
    isLoading: updateLuminaire.isLoading,
    isSuccess: updateLuminaire.isSuccess,
    isError: updateLuminaire.isError,
    error: updateLuminaire.error as AxiosError,
  }
}

export const useDeleteLuminaire = () => {
  const queryClient = useQueryClient()

  const deleteLuminaire = useMutation({
    mutationFn: ({
      lumId,
      callbackFunc,
    }: {
      lumId: string
      callbackFunc?: CallbackFunction
    }) =>
      luminaireAPI.deleteLuminaire(lumId).then((result) => {
        queryClient.invalidateQueries('luminaires')
        if (callbackFunc) {
          callbackFunc({ result, error: undefined })
        }
      }).catch((error) => {
        if (callbackFunc) {
          callbackFunc({ result: undefined, error })
        }
        return Promise.reject(error)
      }),
    onSettled: async () => {
      queryClient.invalidateQueries('luminaires')
    },
  })

  return {
    mutate: deleteLuminaire.mutate,
    isLoading: deleteLuminaire.isLoading,
    isSuccess: deleteLuminaire.isSuccess,
    isError: deleteLuminaire.isError,
    error: deleteLuminaire.error as AxiosError,
  }
}
